// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { Loader, Text, View } from 'reshaped';

import { selectCurrentId } from '@gandi/redux-sharing';
import { Button } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import {
  selectCustomersCount,
  customersResource,
  fetchCustomersList,
  selectCustomersList,
} from 'src/Cart/Customer/modules/customers';
import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';

import type { Props } from '../../hooks/useModalType';

import { CustomerSelect } from './CustomerSelect';

export function CustomerTab({ setTabs, ...props }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const sharingId = useAppSelector(selectCurrentId);

  /// XXX: we are forcing the should fetch to avoid incomplete list on mount after a search
  const shouldFetch = true;
  // useAppSelector(state =>
  //   customersResource.selectShouldFetch(state, sharingId),
  // );

  const customers = useAppSelector(selectCustomersList);
  const isFetching = useAppSelector(state =>
    customersResource.selectIsFetching(state, sharingId),
  );
  const isLoaded = useAppSelector(state =>
    customersResource.selectIsLoaded(state, sharingId),
  );
  const isFailure = useAppSelector(state =>
    customersResource.selectIsFailure(state, sharingId),
  );
  const totalCount = useAppSelector(selectCustomersCount);
  const { cartOwnerOrganization } = useCartSummaryContext();

  const totalCountRef = React.useRef<number>(0);
  if (totalCount != null && totalCount > totalCountRef.current) {
    totalCountRef.current = totalCount;
  }

  const isListEmpty = !totalCountRef.current && customers.length === 0;

  React.useEffect(() => {
    setTabs('customers');
  }, [setTabs]);

  React.useEffect(() => {
    if (shouldFetch && cartOwnerOrganization) {
      dispatch(fetchCustomersList(cartOwnerOrganization.id));
    }
  }, [shouldFetch, cartOwnerOrganization, dispatch]);

  function handleCreateCustomer() {
    props.openModal({
      type: 'createAccountModal',
      currentRole: 'CUSTOMER',
      actionOnSuccess: 'onBack',
    });
  }

  if (isFetching && !isLoaded && !isFailure) {
    return (
      <View justify="center" direction="row">
        <Loader size="large" ariaLabel={__('Loading...')} />
      </View>
    );
  }

  if (isListEmpty) {
    return (
      <View gap={4}>
        <Text as="p" color="neutral-faded">
          {__(
            'I’m buying products for a current or future customer. These products will be managed by my reseller organization. For web hosting products, the reseller organization is the owner.',
          )}
        </Text>
        <View>
          <Button size="large" color="primary" onClick={handleCreateCustomer}>
            {__('Add a new customer')}
          </Button>
        </View>
      </View>
    );
  }

  return (
    <CustomerSelect
      setTabs={setTabs}
      initialTotalCount={totalCountRef.current}
      {...props}
    />
  );
}
